import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { downloadPdf, apiError } from "@/lib/api";
import { fmtDate } from "@/lib/helpers";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Printer, ArrowLeft, FileDown, Ban, Archive, ScanLine, CheckCircle2, Circle } from "lucide-react";
import ScanPalette, { parsePaletteCode, feedback } from "@/components/ScanPalette";

export default function DetailBL() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [bl, setBl] = useState(null);
  const [openCancel, setOpenCancel] = useState(false);
  const [motif, setMotif] = useState("");
  const [busy, setBusy] = useState(false);
  const [controle, setControle] = useState(false);
  const [checked, setChecked] = useState({});
  const [lastScan, setLastScan] = useState(null);

  useEffect(() => {
    api.get(`/bl/${id}`).then((r) => setBl(r.data));
  }, [id]);

  const handleScan = (raw) => {
    const code = parsePaletteCode(raw);
    if (!code) { feedback(false); return setLastScan({ ok: false, message: `Code non reconnu : ${String(raw).slice(0, 30)}` }); }
    const ligne = bl.lignes.find((l) => l.code_qr === code);
    if (!ligne) { feedback(false); return setLastScan({ ok: false, message: `${code} n'est PAS sur ce bon de livraison !` }); }
    if (checked[code]) { feedback(true); return setLastScan({ ok: true, message: `${code} déjà contrôlée` }); }
    feedback(true);
    setChecked((c) => ({ ...c, [code]: true }));
    setLastScan({ ok: true, message: `${code} contrôlée — ${ligne.batiment} · ${ligne.poids_net.toFixed(1)} kg` });
  };

  if (!bl) return <div className="text-slate-400">Chargement…</div>;

  const isDec = bl.type === "DECLASSEE";
  const annule = bl.statut === "annule";
  const nbChecked = Object.keys(checked).length;
  const complet = nbChecked === bl.lignes.length;

  const pdf = async () => {
    setBusy(true);
    try {
      await downloadPdf(`/bl/${id}/pdf`, `${bl.numero}${annule ? "_ANNULE" : ""}.pdf`);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const annuler = async () => {
    if (!motif.trim()) return toast.error("Le motif d'annulation est obligatoire.");
    setBusy(true);
    try {
      const r = await api.post(`/bl/${id}/annuler`, { motif: motif.trim() });
      setBl(r.data);
      setOpenCancel(false);
      toast.success(`${bl.numero} annulé — ${bl.nb_palettes} palette(s) remise(s) en stock`);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto" data-testid="detail-bl">
      <div className="no-print flex flex-wrap items-center justify-between gap-3 mb-5">
        <Button variant="outline" onClick={() => navigate("/bl")} className="h-12 font-bold">
          <ArrowLeft className="w-5 h-5 mr-1" /> Historique
        </Button>
        <div className="flex flex-wrap gap-3">
          {user?.role === "admin" && !annule && (
            <Button data-testid="bl-cancel-btn" variant="outline" onClick={() => setOpenCancel(true)}
              className="h-12 font-bold border-rose-300 text-rose-700 hover:bg-rose-50">
              <Ban className="w-5 h-5 mr-1" /> Annuler ce BL
            </Button>
          )}
          {!annule && !controle && (
            <Button data-testid="bl-controle-btn" variant="outline" onClick={() => { setLastScan(null); setControle(true); }}
              className="h-12 font-bold border-emerald-300 text-emerald-800 hover:bg-emerald-50">
              <ScanLine className="w-5 h-5 mr-1" /> Contrôler le chargement
            </Button>
          )}
          <Button data-testid="bl-pdf-btn" variant="outline" onClick={pdf} disabled={busy} className="h-12 font-bold">
            <FileDown className="w-5 h-5 mr-1" /> PDF archivé
          </Button>
          <Button data-testid="bl-print-btn" onClick={() => window.print()} className="h-12 font-bold bg-slate-900 hover:bg-slate-800">
            <Printer className="w-5 h-5 mr-1" /> Imprimer
          </Button>
        </div>
      </div>

      {annule && (
        <div data-testid="bl-annule-banner" className="no-print mb-4 rounded-xl border-2 border-rose-500 bg-rose-50 p-4">
          <div className="font-heading font-black text-rose-700 uppercase tracking-wide text-lg">BL annulé</div>
          <div className="text-sm text-rose-900">
            Le {fmtDate(bl.annulation.at.slice(0, 10))} par <b>{bl.annulation.by}</b> — Motif : {bl.annulation.motif}.
            Les palettes sont revenues en stock.
          </div>
        </div>
      )}

      {controle && (
        <div className="no-print mb-4 space-y-3">
          <ScanPalette onCode={handleScan} onClose={() => setControle(false)} lastResult={lastScan}
            count={nbChecked} total={bl.lignes.length} countLabel="palettes contrôlées"
            hint="Scannez chaque palette au chargement : le compteur avance, une palette étrangère au BL est refusée." />
          <div data-testid="bl-controle-status" className={`rounded-xl border-2 px-4 py-3 font-bold flex items-center gap-2 ${complet ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "border-slate-200 bg-white text-slate-700"}`}>
            {complet ? <CheckCircle2 className="w-5 h-5" /> : <Circle className="w-5 h-5 text-slate-300" />}
            {complet ? `Chargement complet : ${nbChecked}/${bl.lignes.length} palettes contrôlées` : `${nbChecked}/${bl.lignes.length} palettes contrôlées — ${bl.lignes.length - nbChecked} restante(s)`}
          </div>
        </div>
      )}

      {bl.pdf_archived_at && (
        <div data-testid="bl-archive-info" className="no-print mb-4 text-xs font-semibold text-slate-500 flex items-center gap-1.5">
          <Archive className="w-4 h-4" /> PDF archivé le {fmtDate(bl.pdf_archived_at.slice(0, 10))} à {bl.pdf_archived_at.slice(11, 16)} UTC
        </div>
      )}

      <Dialog open={openCancel} onOpenChange={setOpenCancel}>
        <DialogContent data-testid="bl-cancel-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase">Annuler le BL {bl.numero}</DialogTitle>
            <DialogDescription>
              Le bon de livraison sera conservé dans l'historique avec la mention ANNULÉ.
              Les {bl.nb_palettes} palette(s) reviendront en stock pour être réexpédiées.
            </DialogDescription>
          </DialogHeader>
          <Textarea data-testid="bl-cancel-motif" placeholder="Motif d'annulation (obligatoire)"
            value={motif} onChange={(e) => setMotif(e.target.value)} className="min-h-[96px]" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenCancel(false)} className="h-12 font-bold">Retour</Button>
            <Button data-testid="bl-cancel-confirm-btn" onClick={annuler} disabled={busy || !motif.trim()}
              className="h-12 font-bold bg-rose-600 hover:bg-rose-700">
              <Ban className="w-5 h-5 mr-1" /> Confirmer l'annulation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Printable document */}
      <div className={`print-page bg-white border-2 p-8 mx-auto relative ${annule ? "border-rose-400" : "border-slate-300"}`} style={{ maxWidth: "800px" }}>
        {annule && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="font-heading font-black text-[110px] text-rose-600/15 rotate-[-30deg] uppercase select-none">Annulé</div>
          </div>
        )}
        {isDec && (
          <div className="bg-rose-600 text-white font-heading font-black text-center py-2 uppercase tracking-wide -mx-8 -mt-8 mb-6 px-8">
            Bon de livraison — Palettes déclassées
          </div>
        )}

        <div className="flex justify-between gap-6 mb-6">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-400 font-bold">Expéditeur</div>
            <div className="font-heading font-black text-xl text-slate-900">{bl.expediteur.nom}</div>
            <div className="text-slate-600 text-sm">{bl.expediteur.adresse}</div>
            <div className="text-slate-600 text-sm">{bl.expediteur.cp_ville}</div>
          </div>
          <div className="text-right">
            <div className="font-heading font-black text-2xl text-slate-900">
              {bl.numero}{annule && <span className="text-rose-600"> — ANNULÉ</span>}
            </div>
            <div className="text-xs uppercase tracking-wide text-slate-400 font-bold mt-2">Destinataire</div>
            <div className="font-bold text-slate-900">{bl.destinataire.nom}</div>
            <div className="text-slate-600 text-sm">{bl.destinataire.adresse}</div>
            <div className="text-slate-600 text-sm">{bl.destinataire.cp_ville}</div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-6 text-sm border-y-2 border-slate-200 py-3">
          <Info label="Date de départ" value={fmtDate(bl.date_depart)} />
          <Info label="Transporteur" value={bl.transporteur || "—"} />
          <Info label="N° véhicule" value={bl.num_vehicule || "—"} />
        </div>

        <table className="w-full text-sm mb-6">
          <thead>
            <tr className="bg-slate-100 text-left text-xs uppercase tracking-wide text-slate-500 font-bold">
              <th className="px-3 py-2">N°</th>
              <th className="px-3 py-2">Réf palette</th>
              <th className="px-3 py-2">Bâtiment</th>
              <th className="px-3 py-2">Ponte</th>
              {isDec && <th className="px-3 py-2">Motif</th>}
              <th className="px-3 py-2 text-right">P. brut</th>
              <th className="px-3 py-2 text-right">P. net</th>
            </tr>
          </thead>
          <tbody>
            {bl.lignes.map((l, i) => (
              <tr key={l.palette_id} data-testid={`bl-ligne-${l.code_qr}`} className={`border-b border-slate-100 ${checked[l.code_qr] ? "bg-emerald-50" : ""}`}>
                <td className="px-3 py-2">
                  {checked[l.code_qr] ? <CheckCircle2 data-testid={`bl-ligne-checked-${l.code_qr}`} className="w-4 h-4 text-emerald-600 inline no-print" /> : null} {i + 1}
                </td>
                <td className="px-3 py-2 font-mono font-bold">{l.code_qr}</td>
                <td className="px-3 py-2">{l.batiment_code}</td>
                <td className="px-3 py-2">{fmtDate(l.date_ponte)}</td>
                {isDec && <td className="px-3 py-2 text-rose-700 font-bold">{l.motif}</td>}
                <td className="px-3 py-2 text-right font-mono">{l.poids_brut.toFixed(1)}</td>
                <td className="px-3 py-2 text-right font-mono font-bold">{l.poids_net.toFixed(1)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="flex justify-end">
          <div className="text-right space-y-1">
            <div className="text-sm text-slate-500 font-bold">Total : {bl.nb_palettes} palettes</div>
            <div className="text-sm text-slate-500">Poids brut total : <span className="font-mono font-bold text-slate-900">{bl.poids_brut_total.toFixed(1)} kg</span></div>
            <div className="text-lg">Poids net total : <span className="font-mono font-black text-slate-900">{bl.poids_net_total.toFixed(1)} kg</span></div>
          </div>
        </div>

        {isDec && (
          <div className="mt-6 border-2 border-rose-600 bg-rose-50 text-rose-800 font-bold text-center py-2 px-4 uppercase text-sm">
            Lot destiné à la casserie / transformation industrielle, non consommable en l'état
          </div>
        )}

        <div className="mt-8 flex justify-between gap-6">
          <div className="border-2 border-dashed border-slate-300 rounded-lg flex items-center justify-center text-[10px] text-slate-400 text-center p-2 shrink-0" style={{ width: "62mm", height: "100mm" }}>
            Emplacement étiquette de contrôle (62×100 mm)
          </div>
          <div className="flex-1 border-t-2 border-slate-300 pt-2 self-end">
            <div className="text-xs uppercase tracking-wide text-slate-400 font-bold">Signature chauffeur / émargement</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-slate-400 font-bold">{label}</div>
      <div className="font-bold text-slate-900">{value}</div>
    </div>
  );
}
